import React, { FC, ReactElement, useState } from "react";

import { useMsal } from "@azure/msal-react";

import { DeleteOutline } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Checkbox,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import {
  addUserFormatPrompt,
  addUserFormats,
  getType,
  getUserFormatPrompt,
} from "../../redux/slices/typeSlice";
import { loadingforUserprompts } from "../../redux/slices/loadingSlice";

import { DELETE_USER_FORMAT, UPDATE_USER_FORMAT } from "../../endpoint";
import apiCall from "../../utils/apiCall";
import { addError } from "../../redux/slices/errorSlice";
import ConfirmDeleteModal from "../ConfirmDelete";

interface i_UserFormat {
  value: string;
}

const UserFormat: FC<i_UserFormat> = ({ value }): ReactElement => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const dispatch = useAppDispatch();

  const { accounts } = useMsal();
  const account = accounts[0];

  const chatType = useAppSelector(getType);
  const userFormatPrompt = useAppSelector(getUserFormatPrompt);

  const includePrompt = (e: any, index: string) => {
    if (e.target.checked) {
      dispatch(addUserFormatPrompt(index));
    } else {
      dispatch(addUserFormatPrompt(""));
    }
  };

  const deleteUserFormat = (index: string) => {
    dispatch(loadingforUserprompts(true));
    apiCall("POST", DELETE_USER_FORMAT, {
      prompt: index,
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((res) => {
        dispatch(addUserFormats(res.result));
      })
      .catch(() => {
        dispatch(addError("Failed to delete the prompt.Please try again"));
      })
      .finally(() => {
        dispatch(loadingforUserprompts(false));
      });
  };

  const updateUserFormat = () => {
    dispatch(loadingforUserprompts(true));
    apiCall("POST", UPDATE_USER_FORMAT, {
      old_prompt: value,
      new_prompt: editValue,
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((res) => {
        setEditing(false);
        dispatch(addUserFormats(res.result));
      })
      .catch(() => {
        dispatch(
          addError("Failed to update the prompt value.Please try again.")
        );
      })
      .finally(() => {
        dispatch(loadingforUserprompts(false));
      });
  };

  return (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: "initial",
          alignItems: "start",
          //   px: 2.5,
        }}
      >
        <Checkbox
          id="promptcheckbox"
          defaultChecked
          color="success"
          sx={{
            m: 0,
            p: 0,
            mr: 0.5,
            mt: 0.4,
          }}
          size="small"
          checked={userFormatPrompt === value}
          onChange={(e) => includePrompt(e, value)}
          aria-label="checkbox icon user"
        />
        {editing ? (
          <TextField
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
            }}
            size="small"
            multiline
            maxRows={3}
            minRows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter") updateUserFormat();
            }}
            onBlur={() => {
              updateUserFormat();
            }}
          />
        ) : (
          <ListItemText
            id="promptvalue"
            primary={editValue}
            sx={{
              opacity: 1,
              textWrap: "wrap",
              p: 0,
              m: 0,
              px: 0.3,
              wordBreak: "break-word",
            }}
            aria-label={editValue}
          />
        )}
        {editing ? (
          <TaskAltIcon
            color="success"
            fontSize="small"
            onClick={() => updateUserFormat()}
            sx={{
              mt: 0.4,
              cursor: "pointer",
            }}
            aria-label="done icon user"
          />
        ) : (
          <Tooltip title="Edit">
          <EditIcon
            id="editicon"
            color="primary"
            fontSize="small"
            onClick={() => setEditing(true)}
            sx={{
              mt: 0.4,
              cursor: "pointer",
            }}
            aria-label="edit icon user"
          />
          </Tooltip>
        )}
        <Tooltip title="Delete">
        <DeleteOutline
          id="deleteicon"
          color="primary"
          fontSize="small"
          onClick={() => setShowConfirmDelete(true)}
          sx={{
            mt: 0.4,
          }}
          aria-label="delete icon user"
        />
        </Tooltip>
      </ListItemButton>
      <Divider />
      <ConfirmDeleteModal
        show={showConfirmDelete}
        cancel={() => setShowConfirmDelete(false)}
        confirm={() => deleteUserFormat(value)}
      />
    </ListItem>
  );
};

export default UserFormat;
